import {
	RadioGroup,
	FormControlLabel,
	Typography,
	RadioGroupProps,
	FormControlLabelProps,
	RadioProps,
} from "@mui/material";
import { CoreRadio } from "./Radio";
import { FormControlTooltip, FormControlTooltipProps } from "../tooltip";

export type RadioOption = {
	value: string;
	label: React.ReactElement | string;
	disabled?: boolean;
	// Could extend this with props to style individual radio buttons
};

type CoreRadioGroupProps = Omit<
	RadioGroupProps,
	"row" | "onChange" | "value" | "defaultValue" | "aria-label" | "aria-labelledby"
> & {
	radioOptions: RadioOption[];
	onChange: (event: React.ChangeEvent<HTMLInputElement>, value: string) => void;
	/**Font size of label of FormControlLabel */
	radioLabelFontSize?: string | number;
	/**Font size of CoreRadio */
	coreRadioSize?: string | number;
	row?: boolean;
	disabled?: boolean;
	/**Pass either `radioGroupAriaLabelledby` or `radioGroupAriaLabel`. never both */
	radioGroupAriaLabelledby?: string;
	/**Pass either `radioGroupAriaLabelledby` or `radioGroupAriaLabel`. never both */
	radioGroupAriaLabel?: string;
	selectedValue?: string;
	/**Applies to all FormControlLabel */
	formControlLabelProps?: Omit<Partial<FormControlLabelProps>, "control" | "label">;
	/**Applies to all CoreRadios */
	radioProps?: Omit<
		Partial<RadioProps>,
		"value" | "disabled" | "checked" | "onChange" | "size" | "aria-label" | "aria-labelledby"
	>;
} & Pick<FormControlTooltipProps<false>, "tooltip" | "disabledTooltip" | "slotProps">;

export const CoreRadioGroup = ({
	radioOptions,
	onChange,
	selectedValue,
	radioLabelFontSize,
	coreRadioSize,
	row = true,
	disabled,
	tooltip,
	disabledTooltip,
	radioGroupAriaLabelledby,
	radioGroupAriaLabel,
	formControlLabelProps,
	radioProps,
	slotProps,
	...radioGroupProps
}: CoreRadioGroupProps) => {
	return (
		<FormControlTooltip
			tooltip={tooltip}
			disabled={disabled}
			disabledTooltip={disabledTooltip}
			wrapDisabledChild={false}
			slotProps={slotProps}
		>
			<RadioGroup
				row={row}
				onChange={onChange}
				value={selectedValue}
				{...radioGroupProps}
				aria-label={radioGroupAriaLabel}
				aria-labelledby={radioGroupAriaLabelledby}
			>
				{radioOptions.map((option) => {
					const radioLabel =
						typeof option.label === "string" ? (
							<Typography fontSize={radioLabelFontSize ?? 15} sx={{ margin: 0 }}>
								{option.label}
							</Typography>
						) : (
							option.label
						);
					return (
						<FormControlLabel
							{...formControlLabelProps}
							key={option.value}
							label={radioLabel}
							control={
								<CoreRadio
									key={option.value}
									value={option.value}
									disabled={disabled || option.disabled}
									forceTabIndex={false}
									size={coreRadioSize}
									{...radioProps}
								/>
							}
						/>
					);
				})}
			</RadioGroup>
		</FormControlTooltip>
	);
};
